import { isEqual, isNil, isEmpty, omitBy, defaults, keyBy } from 'lodash';
import { toBoolean, toInteger } from '@/utils';

function defaultSerialize(value) {
  return (Array.isArray(value)) ?
    value.join(',') : value;
}

function defaultDeserialize(value, type = '') {
  switch (type) {
    case 'select':
      return value.split(',');
    default:
      return value;
  }
}

export default {
  computed: {
    attributesByKey() { return keyBy(this.config.attributesDefs, 'key'); },
    columnsByKey() { return keyBy(this.columns, 'key'); },
  },
  created() {
    this.$path = this.$route.path;
    this.setDataFromRoute();

    this.unwatchSetRouteFromData = this.$watch(
      vm => ((vm.resources.pagination, vm.resources.sort, vm.resources.filters)),
      this.setRouteFromData,
    );
  },
  beforeDestroy() { this.unwatchSetRouteFromData(); },
  watch: {
    $route: 'onRouteChange',
  },
  methods: {
    onRouteChange() {
      if (this.$path !== this.$route.path) return;
      this.setDataFromRoute();
    },

    setDataFromRoute() {
      this.setPaginationFromRoute();
      this.setSortFromRoute();
      this.setFiltersFromRoute();
    },

    setPaginationFromRoute() {
      const { _page: page, _limit: per } = this.$route.query;
      const pagination = defaults(omitBy({
        page: toInteger(page),
        per: toInteger(per),
      }, isNil), this.resources.pagination);
      if (!isEqual(pagination, this.resources.pagination)) this.setPagination(pagination);
    },
    setSortFromRoute() {
      let { _sort: columns, _order: order = '' } = this.$route.query;
      if (!columns) return;
      columns = columns.split(',');
      order = order.split(',');
      const sort = columns.map((attr, index) => [attr, order[index]]);
      if (!isEqual(sort, this.resources.sort)) this.setSort(sort);
    },
    setFiltersFromRoute() {
      const { query } = this.$route;
      const filters = {
        deleted: false,
      };
      Object.entries(query).forEach(([key, data]) => {
        switch (key) {
          case '_deleted':
            filters.deleted = toBoolean(data);
            break;
          default: {
            if (key.startsWith('_')) return; // control key
            if (isNil(data) || data === '') return;

            const [attr, op] = key.split('_');
            const config = this.attributesByKey[attr];
            if (!config) return;

            const { routeFilter = {}, table: { filter = {} } = {} } = config;
            const { type = '' } = filter;
            const { deserialize = defaultDeserialize } = routeFilter;

            // if multiple query with same key only take first
            const singleData = Array.isArray(data) ? data[0] : data;

            const value = deserialize(singleData, type);
            if (isNil(value) || value === '') return;

            Object.assign(filters, {
              [attr]: (op ? { op, query: value } : value),
            });
          }
        }
      });

      if (!isEqual(filters, this.resources.filters)) {
        this.setFilters(filters);
      }
    },

    setRouteFromData() {
      const query = {
        ...this.getPaginationQuery(this.resources.pagination),
        ...this.getSortQuery(this.resources.sort),
        ...this.getFiltersQuery(this.resources.filters),
      };

      if (!isEqual(query, this.$route.query)) {
        this.$router.push({ ...this.$route, query, replace: true });
      }
    },
    getPaginationQuery({ page = '', per = '' }) {
      return omitBy({ _page: String(page), _limit: String(per) }, isEmpty);
    },
    getSortQuery(sort) {
      if (!sort) return {};
      return omitBy({
        _sort: !sort.length ? null : sort.map(([key]) => key).join(','),
        _order: !sort.length ? null : sort.map(([, order]) => order).join(','),
      }, isNil);
    },
    getFiltersQuery(filters) {
      const filterQuery = {};
      Object.entries(filters).forEach(([key, data]) => {
        if (isNil(data)) return;

        switch (key) {
          case 'deleted':
            if (data) { filterQuery._deleted = 'true'; } // eslint-disable-line no-underscore-dangle
            break;
          default: {
            const config = this.attributesByKey[key];
            const { routeFilter = {} } = config;
            const { serialize = defaultSerialize } = routeFilter;

            const { op, query } = data.op ? data : { query: data };
            const value = serialize(query);

            Object.assign(filterQuery, op ?
              { [`${key}_${op}`]: value }
              : { [key]: value });
          }
        }
      });
      return filterQuery;
    },
  },
};
